import React, { Component } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { buildComparisonCsv } from '../../../util/helpers';

const GROUP_NONE = 'none';
const GROUP_SDK = 'sdkVersion';
const GROUP_TEST_RUN = 'testRun';
const SDK_FILTER_ALL = 'all';

/**
 * Side-by-side comparison of the relevant data of two or more completed test
 * instances.
 *
 * - BD-8219: each selected instance becomes a column, each row a field from
 *   `getComparisonFields`, and rows whose values differ are highlighted.
 * - BD-8220: columns can be grouped by SDK version or by test run (scenario).
 * - BD-8221: instances from multiple SDKs can be compared together, the SDK of
 *   each instance is shown in its column header, and columns can be filtered to
 *   a single SDK.
 * - BD-8222: the current comparison (instances + grouping/SDK filter) can be
 *   saved under a name, updated or deleted.
 * - BD-8223: the comparison data can be exported as CSV or JSON.
 */
class CompareTestInstances extends Component {
    constructor(props) {
        super(props);

        const options = props.options || {};
        this.state = {
            groupBy: options.initialGroupBy || GROUP_NONE,
            sdkFilter: options.initialSdkFilter || SDK_FILTER_ALL,
            savedComparisonId: options.savedComparisonId || null,
            nameInput: options.savedComparisonName || '',
            statusMessage: '',
            statusError: '',
            isSaving: false,
        };
    }

    // Distinct SDK versions across every compared instance, used to populate the
    // SDK filter control (BD-8221).
    getSdkVersions() {
        const versions = [];
        (this.props.instances || []).forEach(instance => {
            const sdkVersion = instance.meta ? instance.meta.sdkVersion : undefined;
            if (sdkVersion && versions.indexOf(sdkVersion) === -1) {
                versions.push(sdkVersion);
            }
        });
        return versions;
    }

    // Instances that pass the current SDK filter (BD-8221).
    getVisibleInstances() {
        const { instances } = this.props;
        const { sdkFilter } = this.state;
        if (!instances) return [];
        if (sdkFilter === SDK_FILTER_ALL) return instances;
        return instances.filter(instance => instance.meta && instance.meta.sdkVersion === sdkFilter);
    }

    // Order the visible instances into groups for rendering (BD-8220). Each group
    // is `{ label, instances }`. With no grouping a single unlabelled group is
    // returned so the render path is uniform.
    getGroups(visibleInstances) {
        const { groupBy } = this.state;
        if (groupBy === GROUP_NONE) {
            return [{ key: 'all', label: '', instances: visibleInstances }];
        }

        const groups = [];
        const groupsByKey = {};
        visibleInstances.forEach(instance => {
            const value = (instance.meta && instance.meta[groupBy]) || '—';
            if (!groupsByKey[value]) {
                groupsByKey[value] = { key: value, label: value, instances: [] };
                groups.push(groupsByKey[value]);
            }
            groupsByKey[value].instances.push(instance);
        });
        return groups;
    }

    // A field differs when at least two visible instances report distinct,
    // meaningful values for it.
    valuesDiffer(values) {
        const meaningful = values.filter(value => value !== undefined && value !== null && value !== '' && value !== '—');
        if (meaningful.length <= 1) {
            return false;
        }
        return new Set(meaningful.map(value => String(value))).size > 1;
    }

    setGroupBy = event => {
        this.setState({ groupBy: event.target.value });
    };

    setSdkFilter = event => {
        this.setState({ sdkFilter: event.target.value });
    };

    updateNameInput = event => {
        this.setState({ nameInput: event.target.value });
    };

    // Trigger a client-side download of the given text content (BD-8223).
    downloadFile(filename, content, mimeType) {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    exportCsv = () => {
        const visibleInstances = this.getVisibleInstances();
        if (visibleInstances.length < 2) return;
        const csv = buildComparisonCsv(visibleInstances);
        this.downloadFile(`test-instance-comparison-${Date.now()}.csv`, csv, 'text/csv;charset=utf-8;');
    };

    exportJson = () => {
        const visibleInstances = this.getVisibleInstances();
        if (visibleInstances.length < 2) return;

        const payload = visibleInstances.map(instance => {
            const fields = {};
            (instance.fields || []).forEach(field => {
                fields[field.label] = field.value;
            });
            return {
                id: instance.id,
                name: instance.name || instance.id,
                sdkVersion: instance.meta ? instance.meta.sdkVersion : undefined,
                testRun: instance.meta ? instance.meta.testRun : undefined,
                fields,
            };
        });

        this.downloadFile(
            `test-instance-comparison-${Date.now()}.json`,
            JSON.stringify(payload, null, 2),
            'application/json;charset=utf-8;'
        );
    };

    handleSave = async () => {
        const { onSaveComparison, onUpdateComparison } = this.props;
        const { nameInput, groupBy, sdkFilter, savedComparisonId } = this.state;
        const name = nameInput.trim();

        if (!name) {
            this.setState({ statusError: 'Enter a name to save this comparison.', statusMessage: '' });
            return;
        }

        this.setState({ isSaving: true, statusError: '', statusMessage: '' });

        try {
            if (savedComparisonId) {
                await onUpdateComparison(savedComparisonId, { name, groupBy, sdkFilter });
                this.setState({ isSaving: false, statusMessage: `Updated "${name}".`, statusError: '' });
            } else {
                const saved = await onSaveComparison({ name, groupBy, sdkFilter });
                this.setState({
                    isSaving: false,
                    savedComparisonId: saved && saved._id ? saved._id : null,
                    statusMessage: `Saved "${name}".`,
                    statusError: '',
                });
            }
        } catch (error) {
            this.setState({ isSaving: false, statusError: 'Could not save the comparison.', statusMessage: '' });
        }
    };

    handleSaveAsNew = async () => {
        const { onSaveComparison } = this.props;
        const { nameInput, groupBy, sdkFilter } = this.state;
        const name = nameInput.trim();

        if (!name) {
            this.setState({ statusError: 'Enter a name to save this comparison.', statusMessage: '' });
            return;
        }

        this.setState({ isSaving: true, statusError: '', statusMessage: '' });

        try {
            const saved = await onSaveComparison({ name, groupBy, sdkFilter });
            this.setState({
                isSaving: false,
                savedComparisonId: saved && saved._id ? saved._id : null,
                statusMessage: `Saved "${name}" as a new comparison.`,
                statusError: '',
            });
        } catch (error) {
            this.setState({ isSaving: false, statusError: 'Could not save the comparison.', statusMessage: '' });
        }
    };

    handleDelete = async () => {
        const { onDeleteComparison } = this.props;
        const { savedComparisonId, nameInput } = this.state;

        if (!savedComparisonId) return;
        if (!confirm(`Delete saved comparison "${nameInput}"?`)) return;

        this.setState({ isSaving: true, statusError: '', statusMessage: '' });

        try {
            await onDeleteComparison(savedComparisonId);
            this.setState({
                isSaving: false,
                savedComparisonId: null,
                statusMessage: 'Deleted saved comparison.',
                statusError: '',
            });
        } catch (error) {
            this.setState({ isSaving: false, statusError: 'Could not delete the comparison.', statusMessage: '' });
        }
    };

    renderControls(visibleInstances) {
        const { groupBy, sdkFilter, nameInput, savedComparisonId, isSaving } = this.state;
        const sdkVersions = this.getSdkVersions();
        const canExport = visibleInstances.length >= 2;

        return (
            <div className="comparison-controls">
                <div className="comparison-control">
                    <label className="comparison-control__label">Group by</label>
                    <select className="form-control comparison-control__input" value={groupBy} onChange={this.setGroupBy}>
                        <option value={GROUP_NONE}>None</option>
                        <option value={GROUP_SDK}>SDK Version</option>
                        <option value={GROUP_TEST_RUN}>Test Run</option>
                    </select>
                </div>

                <div className="comparison-control">
                    <label className="comparison-control__label">SDK</label>
                    <select className="form-control comparison-control__input" value={sdkFilter} onChange={this.setSdkFilter}>
                        <option value={SDK_FILTER_ALL}>All SDKs</option>
                        {sdkVersions.map(version => (
                            <option key={version} value={version}>
                                {version}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="comparison-control comparison-control--export">
                    <label className="comparison-control__label">Export</label>
                    <div className="comparison-control__buttons">
                        <a
                            className={'pagination-button comparison-btn comparison-btn--export' + (canExport ? '' : ' not-clickable')}
                            onClick={canExport ? this.exportCsv : undefined}
                        >
                            CSV
                        </a>
                        <a
                            className={'pagination-button comparison-btn comparison-btn--export' + (canExport ? '' : ' not-clickable')}
                            onClick={canExport ? this.exportJson : undefined}
                        >
                            JSON
                        </a>
                    </div>
                </div>

                <div className="comparison-control comparison-control--save">
                    <label className="comparison-control__label">Save comparison</label>
                    <div className="comparison-control__buttons">
                        <input
                            type="text"
                            className="form-control comparison-control__input"
                            placeholder="Comparison name"
                            value={nameInput}
                            onChange={this.updateNameInput}
                        />
                        <a
                            className={'pagination-button comparison-btn comparison-btn--primary compare-toolbar__compare' + (isSaving ? ' not-clickable' : '')}
                            onClick={isSaving ? undefined : this.handleSave}
                        >
                            {savedComparisonId ? 'Update' : 'Save'}
                        </a>
                        {savedComparisonId && (
                            <a
                                className={'pagination-button comparison-btn comparison-btn--secondary' + (isSaving ? ' not-clickable' : '')}
                                onClick={isSaving ? undefined : this.handleSaveAsNew}
                            >
                                Save as new
                            </a>
                        )}
                        {savedComparisonId && (
                            <a
                                className={'pagination-button comparison-btn comparison-btn--danger compare-toolbar__delete' + (isSaving ? ' not-clickable' : '')}
                                onClick={isSaving ? undefined : this.handleDelete}
                            >
                                Delete
                            </a>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    renderTable(visibleInstances) {
        const { groupBy } = this.state;
        const groups = this.getGroups(visibleInstances);
        // Flatten the groups so the body rows follow the grouped column order.
        const orderedInstances = groups.reduce((all, group) => all.concat(group.instances), []);
        const fieldRows = orderedInstances[0].fields;
        const showGroupHeader = groupBy !== GROUP_NONE;

        return (
            <table className="table comparison-table">
                <thead>
                    {showGroupHeader && (
                        <tr>
                            <th className="comparison-field-label" rowSpan={2}>
                                Field
                            </th>
                            {groups.map(group => (
                                <th
                                    key={group.key}
                                    className="comparison-group-header"
                                    colSpan={group.instances.length}
                                >
                                    {group.label}
                                </th>
                            ))}
                        </tr>
                    )}
                    <tr>
                        {!showGroupHeader && <th className="comparison-field-label">Field</th>}
                        {orderedInstances.map(instance => (
                            <th key={instance.id} className="comparison-instance-header user-select-all">
                                <div>{instance.name || instance.id}</div>
                                {instance.meta && instance.meta.sdkVersion && (
                                    <div className="comparison-instance-sdk">SDK {instance.meta.sdkVersion}</div>
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {fieldRows.map((row, rowIndex) => {
                        const values = orderedInstances.map(instance =>
                            instance.fields[rowIndex] ? instance.fields[rowIndex].value : ''
                        );
                        const differ = this.valuesDiffer(values);

                        return (
                            <tr key={row.key} className={differ ? 'comparison-row comparison-row--diff' : 'comparison-row'}>
                                <td className="comparison-field-label">{row.label}</td>
                                {values.map((value, columnIndex) => (
                                    <td
                                        key={orderedInstances[columnIndex].id}
                                        className={differ ? 'comparison-cell comparison-cell--diff' : 'comparison-cell'}
                                    >
                                        <div className="user-select-all">{value}</div>
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }

    renderBody() {
        const { isLoading, error } = this.props;

        if (isLoading) {
            return (
                <div className="table-message">
                    <LoadingSpinner />
                </div>
            );
        }

        if (error) {
            return <div className="table-message comparison-error">{error}</div>;
        }

        const visibleInstances = this.getVisibleInstances();

        if (visibleInstances.length < 2) {
            return (
                <div className="table-message">
                    Select at least two completed test instances to compare.
                </div>
            );
        }

        return (
            <div>
                {this.renderControls(visibleInstances)}
                <div className="comparison-table-wrapper">{this.renderTable(visibleInstances)}</div>
            </div>
        );
    }

    render() {
        const { navigator, isLoading, error } = this.props;
        const { statusMessage, statusError } = this.state;
        const showStatus = !isLoading && !error && (statusMessage || statusError);

        return (
            <div className="compare-test-instances">
                <ol className="breadcrumb">
                    <li>
                        <a onClick={navigator.requestAnalyticsPagination}>Analytics</a>
                    </li>
                    <li className="active">Compare Test Instances</li>
                </ol>

                <div className="test-instance-pagination-main-div compare-container">
                    <div className="search-analytics-test-instance comparison-toolbar">
                        <span className="block-heading">
                            <span className="glyphicon glyphicon-th-list" /> Compare Test Instances
                        </span>
                        <a className="pagination-button compare-back-button" onClick={navigator.requestAnalyticsPagination}>
                            Back to Analytics
                        </a>
                    </div>

                    {showStatus && (
                        <div className={statusError ? 'comparison-status comparison-status--error' : 'comparison-status'}>
                            {statusError || statusMessage}
                        </div>
                    )}

                    {this.renderBody()}
                </div>
            </div>
        );
    }
}

export default CompareTestInstances;
